import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import { SubscriptionPlan } from '../contexts/AuthContext';
import './SettingsPage.css';
import { useNavigate } from 'react-router-dom';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [imageLoadError, setImageLoadError] = useState(false);

  // 获取用户当前的订阅计划
  const currentPlan: SubscriptionPlan = user?.subscription?.plan || 'none';
  const endDate = user?.subscription?.endDate;

  // 重置图片加载错误状态当用户改变时
  useEffect(() => {
    setImageLoadError(false);
  }, [user?.picture]);

  if (!user) {
    return (
      <div className="settings-page">
        <Header />
        <div className="settings-container">
          <div className="settings-content">
            <h1>Please Login First</h1>
            <p>You need to login to access the settings page.</p>
          </div>
        </div>
      </div>
    );
  }

  const formatDateForRenewal = (isoString: string): string => {
    try {
      const date = new Date(isoString);
      // 使用 toLocaleDateString 础定格式（注意：不同浏览器可能有差异）
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'soon';
    }
  };
  // 渲染计划信息
  const renderPlanInfo = () => {
    if (currentPlan === 'none') {
      return (
        <>
          <div className="plan-title">Free Plan</div>
          <div className="plan-description">Unlock all features with a subscription</div>
        </>
      );
    }

    // basic 或 pro
    let planName = 'Basic Plan';
    if (currentPlan === 'pro') {
      planName = 'Pro Plan';
    }

    let renewalText = 'Your plan renews soon';
    if (endDate) {
      try {
        // 使用 date-fns 格式化（推荐）
        const date = new Date(endDate);
        // 注意：如果 endDate 是 UTC，可能需要调整时区，但一般前端展示本地化即可
        renewalText = `Your plan renews ${formatDateForRenewal(endDate)}`;
      } catch (e) {
        // 日期解析失败，保留默认文案
      }
    }

    return (
      <>
        <div className="plan-title">{planName}</div>
        <div className="plan-description">{renewalText}</div>
      </>
    );
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
    }
  };

  const handleUpgradePlan = () => {
    // window.open('/pricing', '_blank');
    window.open('https://billing.stripe.com/p/login/9B66oH3ogezS6B64EH8EM00', '_blank');
    // navigate('/pricing')
  };

  const handleBuyCredits = () => {
    navigate('/pricing')
    // window.open('/pricing', '_blank'); // Replace with actual credits purchase URL
  };

  const handleJoinDiscord = () => {
    window.open('https://discord.gg/QU6KG2hH', '_blank'); // Replace with actual Discord invite
  };

  // 处理头像图片加载错误
  const handleImageError = () => {
    setImageLoadError(true);
  };

  // 检查是否应该显示Google头像
  const shouldShowGoogleAvatar = () => {
    return user?.picture && user?.isGoogleUser && !imageLoadError;
  };

  // 生成默认头像（使用英文名首字母）
  const getDefaultAvatar = () => {
    // 对于Google用户，优先使用Google提供的名称
    if (user?.isGoogleUser && user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user?.displayName) {
      return user.displayName.charAt(0).toUpperCase();
    }
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // 获取显示名称
  const getDisplayName = () => {
    // 对于Google用户，优先使用Google提供的名称
    if (user?.isGoogleUser && user?.name) {
      return user.name;
    }
    return user?.displayName || user?.name || user?.email?.split('@')[0] || '用户';
  };

  return (
    <div className="settings-page">
      <Header />
      
      <div className="main-container">
        <div className='set-container'> 
          <div className="settings-content">
            {/* User Profile Section */}
            <div className="profile-section">
              <div className="profile-container">
                <div className="profile-image">
                  {shouldShowGoogleAvatar() ? (
                    <img 
                      src={user.picture} 
                      alt={getDisplayName()}
                      className="avatar-image"
                      onError={handleImageError}
                      onLoad={() => setImageLoadError(false)}
                    />
                  ) : (
                    <div className="avatar-placeholder">
                      {getDefaultAvatar()}
                    </div>
                  )}
                </div>

                <div className="profile-info">
                  <div className="user-name">
                    {getDisplayName()}
                  </div>
                  <div className="user-email">
                    {user.email}
                  </div>
                </div>
              </div>
                
              <div className="logout" onClick={handleLogout}>
                <div className='icon'>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M8.50615 2.14121C8.84633 2.17597 9.11172 2.46334 9.11172 2.8127C9.11172 3.16205 8.84633 3.44942 8.50615 3.48418L8.43672 3.4877H4.31201C3.85638 3.4877 3.48672 3.85735 3.48672 4.31299V13.6874L3.49111 13.7718C3.53324 14.1879 3.88476 14.5127 4.31201 14.5127H8.43672C8.80951 14.5127 9.11172 14.8149 9.11172 15.1877C9.11172 15.5605 8.80951 15.8627 8.43672 15.8627H4.31201C3.18578 15.8627 2.25939 15.0067 2.14815 13.9098L2.13672 13.6874V4.31299C2.13672 3.11177 3.11079 2.1377 4.31201 2.1377H8.43672L8.50615 2.14121ZM11.3344 5.14795C11.5981 4.88434 12.0254 4.88434 12.289 5.14795L15.664 8.52295C15.9276 8.78655 15.9276 9.21386 15.664 9.47747L12.289 12.8525C12.0254 13.1161 11.5981 13.1161 11.3344 12.8525C11.0708 12.5889 11.0708 12.1615 11.3344 11.8979L13.5573 9.6752H6.74922C6.37643 9.6752 6.07422 9.37298 6.07422 9.0002C6.07422 8.62741 6.37643 8.3252 6.74922 8.3252H13.5573L11.3344 6.10244C11.0708 5.83884 11.0708 5.41155 11.3344 5.14795Z" fill="white"/>
                  </svg>
                </div>
                
                <div className="text">Sign out</div>
              </div>
            </div>

            {/* Free Plan Section */}
            <div className="plan-section">
              <div className="plan-header">
                <div className="plan-image">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <g filter="url(#filter0_dii_388_45152)">
                      <g filter="url(#filter1_d_388_45152)">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M10.1144 2.39052C10.6351 1.86983 11.4793 1.86982 12 2.39052L13.3333 3.72386H15.0572C15.7936 3.72386 16.3905 4.32081 16.3905 5.05719V6.78105L17.7239 8.11438C18.2446 8.63508 18.2446 9.4793 17.7239 10L16.3905 11.3333V13.0572C16.3905 13.7936 15.7936 14.3905 15.0572 14.3905H13.3333L12 15.7239C11.4793 16.2446 10.6351 16.2446 10.1144 15.7239L8.78105 14.3905H7.05719C6.32081 14.3905 5.72386 13.7936 5.72386 13.0572V11.3333L4.39052 10C3.86983 9.4793 3.86982 8.63508 4.39052 8.11438L5.72386 6.78105V5.05719C5.72386 4.32081 6.32081 3.72386 7.05719 3.72386H8.78105L10.1144 2.39052ZM13.27 7.93952C13.4812 7.63789 13.4078 7.2222 13.1062 7.01106C12.8046 6.79991 12.3889 6.87327 12.1777 7.1749L10.2704 9.8996L9.49621 9.22216C9.21912 8.9797 8.79795 9.00778 8.55549 9.28487C8.31304 9.56196 8.34112 9.98314 8.61821 10.2256L9.95154 11.3923C10.094 11.5169 10.2828 11.5751 10.4708 11.5524C10.6587 11.5296 10.8281 11.428 10.9367 11.2729L13.27 7.93952Z" fill="#FFBE3B"/>
                      </g>
                    </g>
                    <defs>
                      <filter id="filter0_dii_388_45152" x="4" y="0" width="14.1152" height="18.1143" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dy="1"/>
                        <feComposite in2="hardAlpha" operator="out"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 0.758432 0 0 0 0 0.52128 0 0 0 0 0.0433284 0 0 0 1 0"/>
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_388_45152"/>
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_388_45152" result="shape"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dy="2"/>
                        <feGaussianBlur stdDeviation="2"/>
                        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
                        <feBlend mode="normal" in2="shape" result="effect2_innerShadow_388_45152"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dy="-2"/>
                        <feGaussianBlur stdDeviation="4"/>
                        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 0.869871 0 0 0 0 0.60761 0 0 0 1 0"/>
                        <feBlend mode="normal" in2="effect2_innerShadow_388_45152" result="effect3_innerShadow_388_45152"/>
                      </filter>
                      <filter id="filter1_d_388_45152" x="4" y="2" width="14.1152" height="15.1143" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dy="1"/>
                        <feComposite in2="hardAlpha" operator="out"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_388_45152"/>
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_388_45152" result="shape"/>
                      </filter>
                    </defs>
                  </svg>
                </div>
                <div className="plan-subscription">SUBSCRIPTION</div>
              </div>
              <div className="plan-info">
                <div className="plan-information">
                  {renderPlanInfo()}
                </div>
                <div className="upgrade-link" onClick={handleUpgradePlan}>
                  Manage Subscription
                </div>
              </div>
            </div>

            <div className="credits-section">
              <div className="credits-header">
                <div className="credits-image">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <g filter="url(#filter0_dii_388_45168)">
                      <g filter="url(#filter1_d_388_45168)">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M10.6667 15.3333C14.3486 15.3333 17.3333 12.3486 17.3333 8.66667C17.3333 4.98477 14.3486 2 10.6667 2C6.98477 2 4 4.98477 4 8.66667C4 12.3486 6.98477 15.3333 10.6667 15.3333ZM9.95956 6.70711L8.70711 7.95956C8.31658 8.35009 8.31658 8.98325 8.70711 9.37377L9.95956 10.6262C10.3501 11.0168 10.9832 11.0168 11.3738 10.6262L12.6262 9.37377C13.0168 8.98325 13.0168 8.35008 12.6262 7.95956L11.3738 6.70711C10.9832 6.31658 10.3501 6.31658 9.95956 6.70711Z" fill="#FFBE3B"/>
                      </g>
                    </g>
                    <defs>
                      <filter id="filter0_dii_388_45168" x="4" y="0" width="13.334" height="17.3335" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dy="1"/>
                        <feComposite in2="hardAlpha" operator="out"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 0.758432 0 0 0 0 0.52128 0 0 0 0 0.0433284 0 0 0 1 0"/>
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_388_45168"/>
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_388_45168" result="shape"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dy="2"/>
                        <feGaussianBlur stdDeviation="2"/>
                        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
                        <feBlend mode="normal" in2="shape" result="effect2_innerShadow_388_45168"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dy="-2"/>
                        <feGaussianBlur stdDeviation="4"/>
                        <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 0.869871 0 0 0 0 0.60761 0 0 0 1 0"/>
                        <feBlend mode="normal" in2="effect2_innerShadow_388_45168" result="effect3_innerShadow_388_45168"/>
                      </filter>
                      <filter id="filter1_d_388_45168" x="4" y="2" width="13.334" height="14.3335" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dy="1"/>
                        <feComposite in2="hardAlpha" operator="out"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_388_45168"/>
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_388_45168" result="shape"/>
                      </filter>
                    </defs>
                  </svg>
                </div>
                <div className="credits-subscription">Credits</div>
              </div>
              <div className="credits-info">
                <div className="credits-information">
                  <div className="credits-title">Monthly credits</div>
                  <div className="credits-details">
                    <div className="credits-amount">{user.credits}</div>
                    <div className="credits-total">
                      / {user.total}
                    </div>
                  </div>
                </div>
                <div className="credits-progress">
                  <div className="progress-bar">
                    <div className="credits-progress-fill" style={{width: `${user.total > 0 ? Math.min(100, (user.credits / user.total) * 100) : 0}%`}}></div>
                  </div>
                </div>  
              </div>
            </div>


            {/* Billing Section */}
            {/* <div className="billing-section">
              <div className="billing-header">
                <h3 className="billing-title">Billing</h3>
              </div>
              <div className="billing-empty">
                <div className="empty-icon">
                  <svg width="48" height="72" viewBox="0 0 48 72" fill="none">
                    <path d="M24 8L40 16V56L24 64L8 56V16L24 8Z" fill="#4A5568" opacity="0.3"/>
                    <path d="M16 24H32V32H16V24Z" fill="#4A5568" opacity="0.5"/>
                    <path d="M16 36H28V40H16V36Z" fill="#4A5568" opacity="0.5"/>
                  </svg>
                </div>
                <h4 className="empty-title">Nothing here yet</h4>
                <p className="empty-description">
                  All your invoices will appear here once they're available.
                </p>
              </div>
            </div> */}

            
          </div>

          {/* Discord Section */}
          <div className="discord-section">
            <div className="discord-info">
              <div className="discord-header">
                
                <div className="discord-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M16.942 4.28a16.3 16.3 0 0 0-4.126-1.3 12.04 12.04 0 0 0-.529.92 15.01 15.01 0 0 0-4.573 0 11.4 11.4 0 0 0-.535-.92 16.26 16.26 0 0 0-4.129 1.3A17.18 17.18 0 0 0 .182 13.218a15.9 15.9 0 0 0 4.963 2.521c.41-.564.773-1.16 1.084-1.785a10.63 10.63 0 0 1-1.706-.83c.143-.106.283-.217.418-.33a11.664 11.664 0 0 0 10.118 0c.135.113.275.224.418.33-.541.326-1.114.606-1.71.83.311.625.674 1.221 1.084 1.785a15.85 15.85 0 0 0 4.963-2.521c.297-3.036-.498-5.674-2.112-8.016zm-7.31 6.39c-.708 0-1.29-.656-1.29-1.456 0-.8.570-1.456 1.29-1.456.72 0 1.296.656 1.29 1.456 0 .8-.57 1.456-1.29 1.456zm4.735 0c-.708 0-1.29-.656-1.29-1.456 0-.8.57-1.456 1.29-1.456.72 0 1.297.656 1.29 1.456 0 .8-.57 1.456-1.29 1.456z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="discord-title">Join our Discord</div>
              </div>
              <div className="discord-description">
                Get support, updates, and share your work<br />
                with the community!
              </div>
            </div>
            <div className="join-discord-link" onClick={handleJoinDiscord}>
              Join now
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SettingsPage;
